import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { xinyiFAQ } from './content'
import type { Lang } from './types'

interface XinyiFAQV2Props {
  currentLang: Lang
}

/**
 * XinyiFAQV2
 * Minimal expandable FAQ — borders only, + / − toggles, all collapsed
 * by default. Includes Schema.org FAQPage micro-data attributes (kept
 * consistent with the SEO behavior of the original XinyiStorePage).
 */
const XinyiFAQV2: React.FC<XinyiFAQV2Props> = ({ currentLang }) => {
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null)

  return (
    <section
      className="bg-white py-16 md:py-24"
      itemScope
      itemType="https://schema.org/FAQPage"
    >
      <div className="mx-auto max-w-[1100px] px-6 md:px-10">
        <div className="mb-10 text-center">
          <p
            className="mb-3 text-[12px] tracking-[0.22em] text-black/65"
            style={{
              fontFamily: "'EB Garamond', serif",
              fontStyle: 'italic',
              fontWeight: 400,
            }}
          >
            FAQ
          </p>
          <h2
            className="text-black"
            style={{
              fontFamily: "'EB Garamond', 'Noto Serif TC', serif",
              fontWeight: 400,
              fontSize: 'clamp(1.75rem, 3vw, 2.5rem)',
              letterSpacing: '0.04em',
              lineHeight: 1.25,
            }}
          >
            {currentLang === 'zh' ? '常見問題' : 'Frequently Asked'}
          </h2>
        </div>

        <div className="space-y-3">
          {xinyiFAQ.map((item, i) => {
            const isOpen = expandedIdx === i
            return (
              <div
                key={i}
                className="border border-black/15"
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
              >
                <button
                  type="button"
                  onClick={() => setExpandedIdx(isOpen ? null : i)}
                  className="flex w-full items-center justify-between px-6 py-4 text-left transition-colors hover:bg-black/[0.02]"
                >
                  <span
                    className="pr-6 text-sm tracking-[0.04em] text-black"
                    style={{
                      fontFamily: "'Noto Serif TC', 'Noto Sans TC', sans-serif",
                      fontWeight: 400,
                    }}
                    itemProp="name"
                  >
                    {item.q[currentLang]}
                  </span>
                  <span className="text-xl text-black/50">{isOpen ? '−' : '+'}</span>
                </button>

                {isOpen && (
                  <div
                    className="border-t border-black/10 px-6 py-6"
                    itemScope
                    itemProp="acceptedAnswer"
                    itemType="https://schema.org/Answer"
                  >
                    <p
                      className="text-[14px] leading-loose text-black/70"
                      style={{
                        fontFamily: "'Noto Serif TC', 'Noto Sans TC', sans-serif",
                        fontWeight: 300,
                        letterSpacing: '0.03em',
                      }}
                      itemProp="text"
                    >
                      {item.a[currentLang]}
                    </p>
                  </div>
                )}
              </div>
            )
          })}

          {/* Link-out to full FAQ */}
          <div className="border border-black/15">
            <Link
              to={`/${currentLang}/faq`}
              onClick={() => window.scrollTo(0, 0)}
              className="flex w-full items-center justify-between px-6 py-4 transition-colors hover:bg-black/[0.02]"
            >
              <span
                className="text-sm tracking-[0.18em] text-black"
                style={{ fontFamily: "'EB Garamond', 'Noto Serif TC', serif" }}
              >
                {currentLang === 'zh' ? '查看完整 FAQ' : 'View full FAQ'}
              </span>
              <span className="text-xl text-black/50">→</span>
            </Link>
          </div>
        </div>
      </div>
    </section>
  )
}

export default XinyiFAQV2
